import { Button } from "@/components/ui/button";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
} from "@/components/ui/sheet";
import { Textarea } from "@/components/ui/textarea";
import { useToast } from "@/components/ui/use-toast";
import { StudentNoteService } from "@/services/api/student-note";
import { getToastErrorContent } from "@/utils/functions/api-error";
import { zodResolver } from "@hookform/resolvers/zod";
import { Loader2Icon } from "lucide-react";
import { useForm } from "react-hook-form";
import * as z from "zod";

const FormSchema = z.object({
  content: z.string().min(1, { message: "Campo obrigatório" }),
});

type FormSchemaType = z.infer<typeof FormSchema>;

type Props = {
  studentId: string;
  isOpen: boolean;
  onClose: () => void;
  onSuccess: () => void;
};

export default function ModalAddStudentNote({
  studentId,
  isOpen,
  onClose,
  onSuccess,
}: Props) {
  const { toast } = useToast();

  const form = useForm<FormSchemaType>({
    resolver: zodResolver(FormSchema),
    defaultValues: { content: "" },
  });

  function handleClose(open: boolean) {
    if (!open) {
      form.reset();
      onClose();
    }
  }

  async function onSubmit(data: FormSchemaType) {
    try {
      await StudentNoteService.create({
        studentId,
        content: data.content,
      });

      toast({
        variant: "success",
        description: "Anotação salva com sucesso.",
      });

      form.reset();
      onSuccess();
      onClose();
    } catch (error) {
      toast(getToastErrorContent(error));
    }
  }

  const { isSubmitting } = form.formState;

  return (
    <Sheet open={isOpen} onOpenChange={handleClose}>
      <SheetContent className="bg-white py-3">
        <SheetHeader>
          <SheetTitle>Nova anotação</SheetTitle>
        </SheetHeader>

        <Form {...form}>
          <form onSubmit={form.handleSubmit(onSubmit)} className="mt-6">
            <FormField
              name="content"
              control={form.control}
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Anotação</FormLabel>
                  <FormControl>
                    <Textarea
                      rows={6}
                      className="text-gray-900 border border-gray-300 resize-none"
                      placeholder="Digite a anotação sobre o aluno..."
                      {...field}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <Button
              size="xlg"
              disabled={isSubmitting}
              className="mt-6 w-full px-8 hover:bg-gray-900"
            >
              {isSubmitting && (
                <Loader2Icon className="mr-2 h-4 w-4 animate-spin" />
              )}
              Salvar
            </Button>
          </form>
        </Form>
      </SheetContent>
    </Sheet>
  );
}
